import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { database } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useMembers } from '../../hooks/useMembers';

const MembersList = () => {
    const { userRole } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialChurchId = queryParams.get('churchId');

    const [selectedChurchId, setSelectedChurchId] = useState(initialChurchId || '');
    const [churches, setChurches] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterGender, setFilterGender] = useState('');
    const [filterBaptized, setFilterBaptized] = useState('');
    const [filterProfession, setFilterProfession] = useState('');
    const [loadingChurches, setLoadingChurches] = useState(true);
    const [churchesError, setChurchesError] = useState(null);

    // Utiliser le hook useMembers pour récupérer les membres
    const {
        members,
        loading: loadingMembers,
        error: membersError,
    } = useMembers(selectedChurchId !== 'all' ? selectedChurchId : null);

    // État local pour gérer les membres filtrés
    const [filteredMembers, setFilteredMembers] = useState([]);

    // Récupérer la liste des églises pour le filtre
    useEffect(() => {
        const fetchChurches = async () => {
            try {
                setLoadingChurches(true);
                const churchesRef = ref(database, 'churches');
                const snapshot = await get(churchesRef);

                if (snapshot.exists()) {
                    const churchesList = [];
                    snapshot.forEach((childSnapshot) => {
                        churchesList.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        });
                    });

                    // Trier les églises par nom
                    churchesList.sort((a, b) => a.nom.localeCompare(b.nom));

                    setChurches(churchesList);

                    // Si aucune église n'est sélectionnée et que l'utilisateur est admin d'église
                    if (!selectedChurchId && userRole?.role === 'church_admin' && userRole?.churchId) {
                        setSelectedChurchId(userRole.churchId);
                    } else if (!selectedChurchId && churchesList.length > 0) {
                        setSelectedChurchId(initialChurchId || 'all');
                    }
                }
            } catch (error) {
                console.error("Erreur lors du chargement des églises:", error);
                setChurchesError("Impossible de charger la liste des églises.");
            } finally {
                setLoadingChurches(false);
            }
        };

        fetchChurches();
    }, [userRole, initialChurchId, selectedChurchId]);

    // Filtrer les membres en fonction des critères
    useEffect(() => {
        if (loadingMembers) return;

        let filtered = [...members];

        // Filtre de recherche textuelle
        if (searchTerm) {
            filtered = filtered.filter(member =>
                (member.nom && member.nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (member.prenom && member.prenom.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (member.telephone && member.telephone.includes(searchTerm)) ||
                (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Filtre par sexe
        if (filterGender) {
            filtered = filtered.filter(member => member.sexe === filterGender);
        }

        // Filtre par baptême
        if (filterBaptized === 'true') {
            filtered = filtered.filter(member => member.baptise === true);
        } else if (filterBaptized === 'false') {
            filtered = filtered.filter(member => member.baptise === false);
        }

        // Filtre par profession
        if (filterProfession) {
            filtered = filtered.filter(member =>
                member.profession && member.profession.toLowerCase() === filterProfession.toLowerCase()
            );
        }

        setFilteredMembers(filtered);
    }, [members, searchTerm, filterGender, filterBaptized, filterProfession, loadingMembers]);

    // Gérer le changement d'église
    const handleChurchChange = (e) => {
        const value = e.target.value;
        setSelectedChurchId(value);

        // Mettre à jour l'URL pour refléter le filtre d'église
        if (value === 'all') {
            navigate('/members');
        } else {
            navigate(`/members?churchId=${value}`);
        }
    };

    // Récupérer la liste des professions uniques
    const uniqueProfessions = [...new Set(members.map(member => member.profession).filter(Boolean))].sort();

    // Réinitialiser tous les filtres
    const resetFilters = () => {
        setSearchTerm('');
        setFilterGender('');
        setFilterBaptized('');
        setFilterProfession('');
    };

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                    <div className="flex items-center">
                        <Link to="/dashboard" className="inline-flex items-center mr-4 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                            <svg className="-ml-1 mr-1 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Tableau de bord
                        </Link>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mt-2">Gestion des membres</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Liste des membres recensés dans le système.
                    </p>
                </div>
                <div className="mt-4 sm:mt-0">
                    <Link
                        to="/members/add"
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        Ajouter un membre
                    </Link>
                </div>
            </div>

            {/* Filtres */}
            <div className="mt-6 bg-white shadow overflow-hidden rounded-md p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {/* Filtre par église */}
                    <div>
                        <label htmlFor="churchFilter" className="block text-sm font-medium text-gray-700">
                            Église
                        </label>
                        <select
                            id="churchFilter"
                            name="churchFilter"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            value={selectedChurchId}
                            onChange={handleChurchChange}
                            disabled={loadingChurches || (userRole?.role === 'church_admin')}
                        >
                            {userRole?.role === 'admin' && (
                                <option value="all">Toutes les églises</option>
                            )}
                            {churches.map(church => (
                                <option key={church.id} value={church.id}>{church.nom}</option>
                            ))}
                        </select>
                    </div>

                    {/* Barre de recherche */}
                    <div>
                        <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                            Recherche
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                id="search"
                                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                placeholder="Nom, prénom, téléphone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Filtres supplémentaires */}
                    <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label htmlFor="genderFilter" className="block text-sm font-medium text-gray-700">
                                Sexe
                            </label>
                            <select
                                id="genderFilter"
                                name="genderFilter"
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                value={filterGender}
                                onChange={(e) => setFilterGender(e.target.value)}
                            >
                                <option value="">Tous</option>
                                <option value="M">Homme</option>
                                <option value="F">Femme</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="baptizedFilter" className="block text-sm font-medium text-gray-700">
                                Baptisé
                            </label>
                            <select
                                id="baptizedFilter"
                                name="baptizedFilter"
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                value={filterBaptized}
                                onChange={(e) => setFilterBaptized(e.target.value)}
                            >
                                <option value="">Tous</option>
                                <option value="true">Oui</option>
                                <option value="false">Non</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="professionFilter" className="block text-sm font-medium text-gray-700">
                                Profession
                            </label>
                            <select
                                id="professionFilter"
                                name="professionFilter"
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                value={filterProfession}
                                onChange={(e) => setFilterProfession(e.target.value)}
                            >
                                <option value="">Toutes</option>
                                {uniqueProfessions.map(profession => (
                                    <option key={profession} value={profession}>{profession}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Bouton de réinitialisation des filtres */}
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={resetFilters}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <svg className="-ml-1 mr-1 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        Réinitialiser les filtres
                    </button>
                </div>
            </div>

            {/* Affichage des erreurs */}
            {(churchesError || membersError) && (
                <div className="mt-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                    <p>{churchesError || membersError}</p>
                </div>
            )}

            {/* Liste des membres */}
            <div className="mt-6">
                {loadingMembers || loadingChurches ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                    </div>
                ) : (
                    <>
                        {filteredMembers.length === 0 ? (
                            <div className="bg-white shadow overflow-hidden rounded-md p-6 text-center">
                                <div className="flex flex-col items-center justify-center p-8">
                                    <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun membre trouvé</h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        {searchTerm || filterGender || filterBaptized || filterProfession ?
                                            "Aucun membre ne correspond à vos critères de recherche." :
                                            "Commencez par ajouter un nouveau membre."}
                                    </p>
                                    <div className="mt-6">
                                        <Link
                                            to="/members/add"
                                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                            </svg>
                                            Ajouter un membre
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="bg-white shadow overflow-hidden rounded-md">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                        <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        {filteredMembers.length} {filteredMembers.length > 1 ? 'membres trouvés' : 'membre trouvé'}
                      </span>
                                        </div>
                                    </div>
                                    <ul className="divide-y divide-gray-200">
                                        {filteredMembers.map((member) => (
                                            <li key={member.uid} className="px-4 py-4 sm:px-6 hover:bg-gray-50 transition duration-150">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0">
                                                            <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-medium ${member.sexe === 'M' ? 'bg-blue-500' : 'bg-pink-500'}`}>
                                                                {member.prenom?.charAt(0)}{member.nom?.charAt(0)}
                                                            </div>
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {member.prenom} {member.nom}
                                                            </div>
                                                            <div className="text-sm text-gray-500 flex flex-col sm:flex-row sm:space-x-2">
                                                                <span>{member.telephone}</span>
                                                                {member.email && (
                                                                    <span className="hidden sm:inline">•</span>
                                                                )}
                                                                <span>{member.email}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex space-x-2">
                                                        <div className="flex flex-col items-end mr-4">
                                                            {member.profession && (
                                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                  {member.profession}
                                </span>
                                                            )}
                                                            {member.baptise && (
                                                                <span className="mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Baptisé(e)
                                </span>
                                                            )}
                                                        </div>
                                                        <Link
                                                            to={`/members/${member.uid}`}
                                                            className="inline-flex items-center p-1.5 border border-transparent rounded-full text-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                            title="Voir le détail"
                                                        >
                                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                                            </svg>
                                                        </Link>
                                                        <Link
                                                            to={`/members/${member.uid}/edit`}
                                                            className="inline-flex items-center p-1.5 border border-transparent rounded-full text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                            title="Modifier"
                                                        >
                                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                            </svg>
                                                        </Link>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default MembersList;
